import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Block,
  CheckCircle,
  Delete,
  Publish,
  Save,
  Sync,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { SecaoProduto } from '@/components/produtos/SecaoProduto'
import {
  checklistProdutoService,
  errosValidacaoChecklist,
  mensagemErroChecklist,
} from '@/services/checklistProdutoService'
import { formatDate } from '@/utils/formatters'
import type { DocumentoTipo } from '@/types/documento'
import type {
  AtualizarRascunhosResponse,
  CategoriaChecklist,
  ChecklistProdutoResumo,
  ConteudoChecklist,
  DiferencaChecklistProduto,
  ErroValidacaoChecklist,
  ItemDefinicaoChecklist,
  MotivoSemChecklist,
  OrigemVersaoChecklist,
  StatusVersaoChecklist,
  VersaoChecklistProduto,
} from '@/types/checklist'

/**
 * Checklist documental VERSIONADO do produto (AN-PP3 §9, aba "Checklist"; substitui o `ChecklistDocumental`).
 *
 * - Leitura para o back-office (vigente, rascunho, diferença, histórico); edição só ADMIN (`podeEditar`) — o
 *   backend devolve 403 de qualquer forma.
 * - **Nada é calculado aqui**: o número da versão (o rascunho já vem como N+1), o status e a diferença
 *   rascunho × vigente (entrou/saiu/alterou, pela chave) vêm do backend. A tela só monta o corpo do PUT.
 * - A **chave** é a identidade do item entre versões: editável só em item novo; trocar o rótulo a preserva.
 *   O mesmo vale para o código da categoria.
 * - Publicar não mexe nas reservas já criadas (seguem a versão delas); "Atualizar rascunhos" é a ação
 *   explícita e auditada que revincula as reservas RASCUNHO à vigente.
 * - **Dupla aprovação (V64, AN-PP6 §3):** "Enviar para aprovação" PROPÕE a versão (PENDENTE_APROVACAO); ela só
 *   vale quando OUTRA pessoa (gerente ou administrador, `podeAprovar`) aprova. Aprovar/Rejeitar ficam
 *   desabilitados para quem propôs (`usuarioId === pendente.propostoPor`) — o backend recusa com 403
 *   `APROVACAO_PELO_PROPONENTE` de qualquer forma; a tela só explica.
 */

const TIPOS: { valor: DocumentoTipo; rotulo: string }[] = [
  { valor: 'CONTRATO', rotulo: 'Contrato' },
  { valor: 'BALANCO_PATRIMONIAL', rotulo: 'Balanço Patrimonial' },
  { valor: 'DRE', rotulo: 'DRE' },
  { valor: 'SPED_CONTABIL', rotulo: 'SPED Contábil' },
  { valor: 'FATURAMENTO', rotulo: 'Faturamento' },
  { valor: 'EXTRATO_BANCARIO', rotulo: 'Extrato bancário' },
  { valor: 'EXTRATO_CONTA', rotulo: 'Extrato de conta' },
  { valor: 'COMPROVANTE_RENDA', rotulo: 'Comprovante de renda' },
  { valor: 'COMPROVANTE_ENDERECO', rotulo: 'Comprovante de endereço' },
  { valor: 'CNPJ', rotulo: 'CNPJ' },
  { valor: 'CPF', rotulo: 'CPF' },
  { valor: 'RG', rotulo: 'RG' },
  { valor: 'NOTA_FISCAL', rotulo: 'Nota fiscal' },
  { valor: 'RECIBO', rotulo: 'Recibo' },
  { valor: 'PROPOSTA', rotulo: 'Proposta' },
  { valor: 'RELATORIO', rotulo: 'Relatório' },
  { valor: 'PLANILHA', rotulo: 'Planilha' },
  { valor: 'APRESENTACAO', rotulo: 'Apresentação' },
  { valor: 'MANUAL', rotulo: 'Manual' },
  { valor: 'OUTRO', rotulo: 'Outro' },
]
const rotuloTipo = (t: string) => TIPOS.find((x) => x.valor === t)?.rotulo ?? t

const ROTULO_STATUS: Record<StatusVersaoChecklist, string> = {
  RASCUNHO: 'Rascunho',
  PENDENTE_APROVACAO: 'Aguardando aprovação',
  PUBLICADA: 'Vigente',
  SUBSTITUIDA: 'Substituída',
  REJEITADA: 'Rejeitada',
}
const COR_STATUS: Record<StatusVersaoChecklist, 'default' | 'success' | 'warning' | 'error'> = {
  RASCUNHO: 'warning',
  PENDENTE_APROVACAO: 'warning',
  PUBLICADA: 'success',
  SUBSTITUIDA: 'default',
  REJEITADA: 'error',
}

const TEXTO_PROPRIA_PROPOSTA =
  'Você enviou esta versão: a aprovação (ou rejeição) é de outra pessoa — gerente ou administrador.'
const ROTULO_ORIGEM: Record<OrigemVersaoChecklist, string> = {
  MIGRACAO_V62: 'Migração',
  EDITOR: 'Editor',
  API_LEGADA: 'API legada',
  SEED: 'Carga',
}

const TEXTO_SEM_CHECKLIST: Record<MotivoSemChecklist, string> = {
  PRODUTO_SEM_CHECKLIST:
    'Este produto não tem checklist documental: a documentação é definida caso a caso pela Originei.',
  PRODUTO_RETIRADO:
    'Produto retirado do catálogo: o checklist não é mais editado. As reservas existentes seguem a versão delas.',
  PRODUTO_DESCONHECIDO:
    'O produto ainda não chegou ao módulo de oportunidades. O checklist fica disponível assim que a sincronização terminar.',
}

const vazio = (): ConteudoChecklist => ({ categorias: [], itens: [] })

const copia = (c: ConteudoChecklist | null | undefined): ConteudoChecklist =>
  c
    ? {
        categorias: [...(c.categorias ?? [])]
          .sort((a, b) => a.ordem - b.ordem)
          .map((x) => ({ ...x })),
        itens: [...(c.itens ?? [])].sort((a, b) => a.ordem - b.ordem).map((x) => ({ ...x })),
      }
    : vazio()

const textoOuNulo = (s: string | null | undefined) => {
  const t = (s ?? '').trim()
  return t ? t : null
}

/** Corpo do PUT: a ordem é a posição na tela; textos aparados; código básico só com `basico`. */
const normalizar = (c: ConteudoChecklist): ConteudoChecklist => ({
  categorias: c.categorias.map((cat, i) => ({ codigo: cat.codigo.trim(), rotulo: cat.rotulo.trim(), ordem: i })),
  itens: c.itens.map((it, i) => ({
    chave: it.chave.trim(),
    rotulo: it.rotulo.trim(),
    tipoDocumento: it.tipoDocumento,
    categoria: it.categoria,
    ordem: i,
    obrigatorio: it.obrigatorio,
    basico: it.basico,
    codigoBasico: it.basico ? textoOuNulo(it.codigoBasico) : null,
    validadeDias: it.validadeDias,
    fonteValidade: textoOuNulo(it.fonteValidade),
    orientacao: textoOuNulo(it.orientacao),
  })),
})

const mover = <T,>(lista: T[], de: number, para: number): T[] => {
  if (para < 0 || para >= lista.length) return lista
  const nova = [...lista]
  const [x] = nova.splice(de, 1)
  nova.splice(para, 0, x)
  return nova
}

// ─── Leitura de uma versão ──────────────────────────────────────────────────

const ConteudoLeitura: React.FC<{ conteudo: ConteudoChecklist }> = ({ conteudo }) => {
  const c = copia(conteudo)
  if (c.itens.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhum documento exigido nesta versão.
      </Typography>
    )
  }
  const semCategoria = c.itens.filter((i) => !c.categorias.some((cat) => cat.codigo === i.categoria))
  const grupos = [
    ...c.categorias.map((cat) => ({ rotulo: cat.rotulo, itens: c.itens.filter((i) => i.categoria === cat.codigo) })),
    ...(semCategoria.length ? [{ rotulo: 'Sem categoria', itens: semCategoria }] : []),
  ].filter((g) => g.itens.length > 0)
  return (
    <Stack spacing={1.5}>
      {grupos.map((g) => (
        <Box key={g.rotulo}>
          <Typography variant="overline" color="text.secondary">
            {g.rotulo}
          </Typography>
          <Stack spacing={0.5}>
            {g.itens.map((i) => (
              <Stack key={i.chave} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600}>
                  {i.rotulo}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {i.chave}
                </Typography>
                <Chip size="small" variant="outlined" label={rotuloTipo(i.tipoDocumento)} />
                {i.obrigatorio ? (
                  <Chip size="small" color="primary" label="Obrigatório" />
                ) : (
                  <Chip size="small" label="Opcional" />
                )}
                {i.basico && <Chip size="small" color="info" label={`Básico ${i.codigoBasico ?? ''}`.trim()} />}
                {i.validadeDias != null && (
                  <Tooltip title={i.fonteValidade ?? ''}>
                    <Chip size="small" color="warning" variant="outlined" label={`Validade ${i.validadeDias} dias`} />
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

// ─── Diferença (vinda do backend) ───────────────────────────────────────────

const Diferenca: React.FC<{
  resumo: ChecklistProdutoResumo
  /** Padrão: rascunho × vigente. Na proposta pendente: pendente × vigente. */
  diferenca?: DiferencaChecklistProduto | null
  nova?: VersaoChecklistProduto | null
  rotulo?: string
}> = ({ resumo, diferenca, nova, rotulo = 'Diferença do rascunho' }) => {
  const d = diferenca === undefined ? resumo.diferencaRascunho : diferenca
  const versaoNova = nova === undefined ? resumo.rascunho : nova
  if (!d) return null
  const rotuloNo = (v: VersaoChecklistProduto | null, chave: string) =>
    v?.conteudo?.itens?.find((i) => i.chave === chave)?.rotulo ?? chave
  const blocos: { titulo: string; cor: 'success' | 'error' | 'warning'; chaves: string[]; versao: VersaoChecklistProduto | null }[] = [
    { titulo: 'Entrou', cor: 'success', chaves: d.entrou ?? [], versao: versaoNova },
    { titulo: 'Saiu', cor: 'error', chaves: d.saiu ?? [], versao: resumo.vigente },
    { titulo: 'Alterou', cor: 'warning', chaves: d.alterou ?? [], versao: versaoNova },
  ]
  const nada = blocos.every((b) => b.chaves.length === 0)
  return (
    <Box aria-label={rotulo} component="section">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Diferença em relação à vigente
      </Typography>
      {nada ? (
        <Typography variant="body2" color="text.secondary">
          Nenhuma diferença em relação à versão vigente.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {blocos
            .filter((b) => b.chaves.length > 0)
            .map((b) => (
              <Stack key={b.titulo} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 64 }}>
                  {b.titulo} ({b.chaves.length})
                </Typography>
                {b.chaves.map((k) => (
                  <Chip key={k} size="small" color={b.cor} variant="outlined" label={rotuloNo(b.versao, k)} />
                ))}
              </Stack>
            ))}
        </Stack>
      )}
    </Box>
  )
}

// ─── Componente ─────────────────────────────────────────────────────────────

interface Props {
  produtoId: string
  /** ADMIN: edita, envia para aprovação, descarta e atualiza rascunhos. Os demais (back-office) só leem. */
  podeEditar: boolean
  /** V64: GERENTE ou ADMIN — aprova/rejeita a proposta pendente de OUTRA pessoa. */
  podeAprovar?: boolean
  /** `sub` do usuário logado: quem propôs não decide a própria proposta. */
  usuarioId?: string | null
}

const ChecklistVersionadoEditor: React.FC<Props> = ({ produtoId, podeEditar, podeAprovar = false, usuarioId = null }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [resumo, setResumo] = useState<ChecklistProdutoResumo | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erroCarga, setErroCarga] = useState<string | null>(null)

  // Edição local do rascunho (ADMIN). `base` é o que veio do backend, para saber se há alteração não salva.
  const [base, setBase] = useState<ConteudoChecklist>(vazio)
  const [edicao, setEdicao] = useState<ConteudoChecklist>(vazio)
  const [chavesFixas, setChavesFixas] = useState<Set<string>>(new Set())
  const [codigosFixos, setCodigosFixos] = useState<Set<string>>(new Set())
  const [errosCampo, setErrosCampo] = useState<ErroValidacaoChecklist[]>([])
  const [erroAcao, setErroAcao] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const [publicarAberto, setPublicarAberto] = useState(false)
  const [motivoPublicar, setMotivoPublicar] = useState('')
  const [descartarAberto, setDescartarAberto] = useState(false)
  const [atualizarAberto, setAtualizarAberto] = useState(false)
  const [motivoAtualizar, setMotivoAtualizar] = useState('')
  const [somenteSemDocumentos, setSomenteSemDocumentos] = useState(true)
  const [resultadoAtualizar, setResultadoAtualizar] = useState<AtualizarRascunhosResponse | null>(null)
  const [versaoAberta, setVersaoAberta] = useState<string | null>(null)
  const [decisao, setDecisao] = useState<'aprovar' | 'rejeitar' | null>(null)
  const [motivoRejeicao, setMotivoRejeicao] = useState('')

  const aplicar = useCallback((r: ChecklistProdutoResumo) => {
    setResumo(r)
    const origem = copia(r.rascunho?.conteudo ?? r.vigente?.conteudo)
    setBase(origem)
    setEdicao(copia(origem))
    // Chaves e códigos que já existem numa versão salva não mudam (identidade entre versões).
    const chaves = new Set<string>()
    const codigos = new Set<string>()
    for (const v of [r.rascunho, r.vigente]) {
      v?.conteudo?.itens?.forEach((i) => chaves.add(i.chave))
      v?.conteudo?.categorias?.forEach((c) => codigos.add(c.codigo))
    }
    setChavesFixas(chaves)
    setCodigosFixos(codigos)
  }, [])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErroCarga(null)
    try {
      aplicar(await checklistProdutoService.obter(produtoId))
    } catch (e) {
      setErroCarga(mensagemErroChecklist(e, 'Não foi possível carregar o checklist do produto.'))
    } finally {
      setCarregando(false)
    }
  }, [produtoId, aplicar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const alterado = useMemo(
    () => JSON.stringify(normalizar(edicao)) !== JSON.stringify(normalizar(base)),
    [edicao, base],
  )

  const erroDe = (campo: string) => errosCampo.find((e) => e.campo === campo)?.mensagem

  // ─── Ações ───

  const salvar = async () => {
    setSalvando(true)
    setErroAcao(null)
    setErrosCampo([])
    try {
      // Concorrência (revisão PP3, achado 7): a revisão do rascunho lido; 0 = não havia rascunho.
      await checklistProdutoService.salvarRascunho(produtoId, normalizar(edicao), resumo?.rascunho?.revisao ?? 0)
      enqueueSnackbar('Rascunho salvo.', { variant: 'success' })
      await carregar()
    } catch (e) {
      setErrosCampo(errosValidacaoChecklist(e))
      setErroAcao(mensagemErroChecklist(e, 'Falha ao salvar o rascunho.'))
    } finally {
      setSalvando(false)
    }
  }

  const publicar = async () => {
    setSalvando(true)
    setErroAcao(null)
    try {
      const v = await checklistProdutoService.publicar(produtoId, { motivo: motivoPublicar.trim(), origem: 'EDITOR' })
      enqueueSnackbar(
        v?.versao != null
          ? `Versão ${v.versao} enviada para aprovação.`
          : 'Versão enviada para aprovação.',
        { variant: 'success' },
      )
      setPublicarAberto(false)
      setMotivoPublicar('')
      await carregar()
    } catch (e) {
      setErrosCampo(errosValidacaoChecklist(e))
      setErroAcao(mensagemErroChecklist(e, 'Falha ao enviar o checklist para aprovação.'))
      setPublicarAberto(false)
    } finally {
      setSalvando(false)
    }
  }

  const decidir = async () => {
    const pendente = resumo?.pendente
    if (!pendente || !decisao) return
    setSalvando(true)
    setErroAcao(null)
    try {
      if (decisao === 'aprovar') {
        // Revisão PP6-valr (A1): aprova ESTA proposta (id) com o conteúdo que a tela mostrou (hash).
        const v = await checklistProdutoService.aprovar(produtoId, pendente.id, {
          versaoRegistro: pendente.versaoRegistro ?? null,
          conteudoHash: pendente.conteudoHash ?? null,
        })
        enqueueSnackbar(`Versão ${v?.versao ?? pendente.versao} aprovada e publicada.`, { variant: 'success' })
      } else {
        await checklistProdutoService.rejeitar(produtoId, pendente.id, {
          motivo: motivoRejeicao.trim(),
          versaoRegistro: pendente.versaoRegistro ?? null,
          conteudoHash: pendente.conteudoHash ?? null,
        })
        enqueueSnackbar(`Versão ${pendente.versao} rejeitada.`, { variant: 'success' })
      }
      setDecisao(null)
      setMotivoRejeicao('')
      await carregar()
    } catch (e) {
      setErroAcao(mensagemErroChecklist(e, decisao === 'aprovar' ? 'Falha ao aprovar.' : 'Falha ao rejeitar.'))
      setDecisao(null)
    } finally {
      setSalvando(false)
    }
  }

  const descartar = async () => {
    setSalvando(true)
    setErroAcao(null)
    try {
      await checklistProdutoService.descartarRascunho(produtoId)
      enqueueSnackbar('Rascunho descartado.', { variant: 'success' })
      setDescartarAberto(false)
      setErrosCampo([])
      await carregar()
    } catch (e) {
      setErroAcao(mensagemErroChecklist(e, 'Falha ao descartar o rascunho.'))
      setDescartarAberto(false)
    } finally {
      setSalvando(false)
    }
  }

  const atualizarRascunhos = async () => {
    setSalvando(true)
    setErroAcao(null)
    setResultadoAtualizar(null)
    try {
      const r = await checklistProdutoService.atualizarRascunhos(produtoId, {
        motivo: motivoAtualizar.trim(),
        somenteSemDocumentos,
      })
      setResultadoAtualizar(r)
      setAtualizarAberto(false)
      setMotivoAtualizar('')
    } catch (e) {
      setErroAcao(mensagemErroChecklist(e, 'Falha ao atualizar as reservas em rascunho.'))
      setAtualizarAberto(false)
    } finally {
      setSalvando(false)
    }
  }

  // ─── Edição ───

  const alterarCategoria = (i: number, m: Partial<CategoriaChecklist>) =>
    setEdicao((p) => ({ ...p, categorias: p.categorias.map((c, idx) => (idx === i ? { ...c, ...m } : c)) }))
  const alterarItem = (i: number, m: Partial<ItemDefinicaoChecklist>) =>
    setEdicao((p) => ({ ...p, itens: p.itens.map((it, idx) => (idx === i ? { ...it, ...m } : it)) }))
  const novaCategoria = () =>
    setEdicao((p) => ({ ...p, categorias: [...p.categorias, { codigo: '', rotulo: '', ordem: p.categorias.length }] }))
  const novoItem = () =>
    setEdicao((p) => ({
      ...p,
      itens: [
        ...p.itens,
        {
          chave: '',
          rotulo: '',
          tipoDocumento: 'OUTRO',
          categoria: p.categorias[0]?.codigo ?? '',
          ordem: p.itens.length,
          obrigatorio: true,
          basico: false,
          codigoBasico: null,
          validadeDias: null,
          fonteValidade: null,
          orientacao: null,
        },
      ],
    }))

  // ─── Render ───

  if (carregando && !resumo) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando checklist do produto" />
      </Box>
    )
  }

  if (erroCarga || !resumo) {
    return (
      <SecaoProduto titulo="Checklist documental">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void carregar()}>
              Tentar novamente
            </Button>
          }
        >
          {erroCarga ?? 'Não foi possível carregar o checklist do produto.'}
        </Alert>
      </SecaoProduto>
    )
  }

  const { vigente, rascunho, historico } = resumo
  const pendente = resumo.pendente ?? null
  const propriaProposta = !!pendente && !!usuarioId && pendente.propostoPor === usuarioId

  if (resumo.semChecklist) {
    return (
      <SecaoProduto titulo="Checklist documental">
        <Alert severity="info">{TEXTO_SEM_CHECKLIST[resumo.semChecklist] ?? resumo.semChecklist}</Alert>
      </SecaoProduto>
    )
  }

  const errosGerais = errosCampo.filter((e) => !/^(itens|categorias)\[\d+\]\./.test(e.campo))

  return (
    <Stack spacing={3}>
      {/* Vigente */}
      <SecaoProduto
        titulo="Versão vigente"
        descricao="O que as reservas novas deste produto cobram do consultor. Reservas já criadas seguem a versão em que nasceram."
        acao={
          vigente ? (
            <Chip color="success" label={`Versão ${vigente.versao}`} />
          ) : undefined
        }
      >
        {vigente ? (
          <>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
              Vigente desde {formatDate(vigente.vigenteDesde)}
              {vigente.motivo ? ` · ${vigente.motivo}` : ''}
            </Typography>
            <ConteudoLeitura conteudo={vigente.conteudo} />
          </>
        ) : (
          <Alert severity="info">
            Nenhuma versão publicada — a reserva deste produto pode ser enviada sem anexos.
          </Alert>
        )}
        {podeEditar && vigente && (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" size="small" startIcon={<Sync />} onClick={() => setAtualizarAberto(true)}>
              Atualizar reservas em rascunho
            </Button>
          </Box>
        )}
        {resultadoAtualizar && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setResultadoAtualizar(null)}>
            Reservas em rascunho atualizadas
            {resultadoAtualizar.versao != null ? ` para a versão ${resultadoAtualizar.versao}` : ''}:{' '}
            {resultadoAtualizar.revinculadas} revinculada(s), {resultadoAtualizar.ignoradas} ignorada(s).
          </Alert>
        )}
      </SecaoProduto>

      {/* Proposta aguardando aprovação (V64) */}
      {pendente && (
        <SecaoProduto
          titulo={`Versão ${pendente.versao} aguardando aprovação`}
          descricao="Ainda não vale para nenhuma reserva: passa a valer quando outra pessoa (gerente ou administrador) aprovar."
          acao={<Chip color="warning" label="Aguardando aprovação" />}
          rodape={
            podeAprovar ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                <Tooltip title={propriaProposta ? TEXTO_PROPRIA_PROPOSTA : ''}>
                  <span>
                    <Button
                      color="error"
                      startIcon={<Block />}
                      onClick={() => setDecisao('rejeitar')}
                      disabled={salvando || propriaProposta}
                    >
                      Rejeitar
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={propriaProposta ? TEXTO_PROPRIA_PROPOSTA : ''}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => setDecisao('aprovar')}
                      disabled={salvando || propriaProposta}
                    >
                      Aprovar versão {pendente.versao}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            ) : undefined
          }
        >
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
            Enviada {pendente.propostoEm ? `em ${formatDate(pendente.propostoEm)} ` : ''}
            {propriaProposta ? 'por você' : ''}
            {pendente.motivo ? ` · ${pendente.motivo}` : ''}
          </Typography>
          {propriaProposta && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {TEXTO_PROPRIA_PROPOSTA}
            </Alert>
          )}
          {/* Sem a seção do rascunho (quem só aprova), o erro da decisão aparece aqui. */}
          {erroAcao && !(podeEditar || rascunho) && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroAcao(null)}>
              {erroAcao}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Diferenca
              resumo={resumo}
              diferenca={resumo.diferencaPendente ?? null}
              nova={pendente}
              rotulo="Diferença da proposta"
            />
          </Box>
          <ConteudoLeitura conteudo={pendente.conteudo} />
        </SecaoProduto>
      )}

      {/* Rascunho */}
      {(podeEditar || rascunho) && (
        <SecaoProduto
          titulo={rascunho ? `Rascunho da versão ${rascunho.versao}` : 'Novo rascunho'}
          descricao={
            podeEditar
              ? 'Edite categorias e itens e salve o rascunho. A chave identifica o item entre versões e só é definida na criação.'
              : 'Alterações ainda não publicadas (somente leitura).'
          }
          acao={rascunho ? <Chip color="warning" label="Rascunho" /> : undefined}
          rodape={
            podeEditar ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                {rascunho && (
                  <Button color="error" startIcon={<Delete />} onClick={() => setDescartarAberto(true)} disabled={salvando}>
                    Descartar rascunho
                  </Button>
                )}
                <Button variant="outlined" startIcon={<Save />} onClick={() => void salvar()} disabled={salvando || !alterado}>
                  {salvando ? 'Salvando…' : 'Salvar rascunho'}
                </Button>
                {rascunho && (
                  <Tooltip
                    title={
                      alterado
                        ? 'Salve o rascunho antes de enviar para aprovação'
                        : pendente
                          ? 'Já há uma versão aguardando aprovação: aprove ou rejeite antes de enviar outra'
                          : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<Publish />}
                        onClick={() => setPublicarAberto(true)}
                        disabled={salvando || alterado || !!pendente}
                      >
                        Enviar versão {rascunho.versao} para aprovação
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            ) : undefined
          }
        >
          {erroAcao && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroAcao(null)}>
              {erroAcao}
              {errosGerais.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {errosGerais.map((e, i) => (
                    <li key={i}>{e.mensagem}</li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          {rascunho && (
            <Box sx={{ mb: 2 }}>
              <Diferenca resumo={resumo} />
            </Box>
          )}

          {!podeEditar && rascunho ? (
            <ConteudoLeitura conteudo={rascunho.conteudo} />
          ) : (
            podeEditar && (
              <Stack spacing={3}>
                {/* Categorias */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Categorias</Typography>
                    <Button size="small" startIcon={<Add />} onClick={novaCategoria}>
                      Nova categoria
                    </Button>
                  </Stack>
                  {edicao.categorias.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma categoria. Crie ao menos uma para agrupar os itens.
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {edicao.categorias.map((cat, i) => {
                      const fixo = codigosFixos.has(cat.codigo)
                      return (
                        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                          <TextField
                            label="Código da categoria"
                            size="small"
                            value={cat.codigo}
                            onChange={(e) => alterarCategoria(i, { codigo: e.target.value })}
                            InputProps={{ readOnly: fixo }}
                            error={!!erroDe(`categorias[${i}].codigo`)}
                            helperText={erroDe(`categorias[${i}].codigo`) ?? (fixo ? 'Fixo' : 'ex.: cadastral')}
                            sx={{ minWidth: 200 }}
                          />
                          <TextField
                            label="Nome da categoria"
                            size="small"
                            value={cat.rotulo}
                            onChange={(e) => alterarCategoria(i, { rotulo: e.target.value })}
                            error={!!erroDe(`categorias[${i}].rotulo`)}
                            helperText={erroDe(`categorias[${i}].rotulo`)}
                            fullWidth
                          />
                          <Stack direction="row">
                            <IconButton size="small" aria-label={`Subir categoria ${cat.rotulo}`} onClick={() => setEdicao((p) => ({ ...p, categorias: mover(p.categorias, i, i - 1) }))} disabled={i === 0}>
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label={`Descer categoria ${cat.rotulo}`} onClick={() => setEdicao((p) => ({ ...p, categorias: mover(p.categorias, i, i + 1) }))} disabled={i === edicao.categorias.length - 1}>
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                            <IconButton size="small" aria-label={`Remover categoria ${cat.rotulo}`} onClick={() => setEdicao((p) => ({ ...p, categorias: p.categorias.filter((_, idx) => idx !== i) }))}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      )
                    })}
                  </Stack>
                </Box>

                {/* Itens */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Itens</Typography>
                    <Button size="small" startIcon={<Add />} onClick={novoItem}>
                      Novo item
                    </Button>
                  </Stack>
                  {edicao.itens.length === 0 && (
                    <Alert severity="info">
                      Nenhum documento exigido — publicada assim, a reserva deste produto pode ser enviada sem anexos.
                    </Alert>
                  )}
                  <Stack spacing={1.5}>
                    {edicao.itens.map((it, i) => {
                      const fixo = chavesFixas.has(it.chave)
                      const campo = (n: string) => `itens[${i}].${n}`
                      const nome = it.rotulo || `item ${i + 1}`
                      return (
                        <Card key={i} variant="outlined" sx={{ p: 2 }} aria-label={`Item ${nome}`}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={5}>
                              <TextField label="Rótulo" size="small" fullWidth value={it.rotulo}
                                onChange={(e) => alterarItem(i, { rotulo: e.target.value })}
                                error={!!erroDe(campo('rotulo'))} helperText={erroDe(campo('rotulo'))} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField label="Chave" size="small" fullWidth value={it.chave}
                                onChange={(e) => alterarItem(i, { chave: e.target.value })}
                                InputProps={{ readOnly: fixo, sx: { fontFamily: 'monospace' } }}
                                error={!!erroDe(campo('chave'))}
                                helperText={erroDe(campo('chave')) ?? (fixo ? 'Fixa: identifica o item entre versões' : 'ex.: contrato-social')} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Stack direction="row" justifyContent="flex-end">
                                <IconButton size="small" aria-label={`Subir ${nome}`} onClick={() => setEdicao((p) => ({ ...p, itens: mover(p.itens, i, i - 1) }))} disabled={i === 0}>
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton size="small" aria-label={`Descer ${nome}`} onClick={() => setEdicao((p) => ({ ...p, itens: mover(p.itens, i, i + 1) }))} disabled={i === edicao.itens.length - 1}>
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                                <IconButton size="small" aria-label={`Remover ${nome}`} onClick={() => setEdicao((p) => ({ ...p, itens: p.itens.filter((_, idx) => idx !== i) }))}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField label="Tipo de documento" select size="small" fullWidth value={it.tipoDocumento}
                                onChange={(e) => alterarItem(i, { tipoDocumento: e.target.value as DocumentoTipo })}
                                error={!!erroDe(campo('tipoDocumento'))} helperText={erroDe(campo('tipoDocumento'))}>
                                {TIPOS.map((t) => (
                                  <MenuItem key={t.valor} value={t.valor}>{t.rotulo}</MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField label="Categoria" select size="small" fullWidth value={it.categoria}
                                onChange={(e) => alterarItem(i, { categoria: e.target.value })}
                                error={!!erroDe(campo('categoria'))} helperText={erroDe(campo('categoria'))}>
                                {edicao.categorias.map((c) => (
                                  <MenuItem key={c.codigo} value={c.codigo}>{c.rotulo || c.codigo}</MenuItem>
                                ))}
                                {it.categoria && !edicao.categorias.some((c) => c.codigo === it.categoria) && (
                                  <MenuItem value={it.categoria}>{it.categoria}</MenuItem>
                                )}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <FormControlLabel
                                  control={<Switch size="small" checked={it.obrigatorio} onChange={(e) => alterarItem(i, { obrigatorio: e.target.checked })} inputProps={{ 'aria-label': `Obrigatório: ${nome}` }} />}
                                  label={<Typography variant="caption">Obrigatório</Typography>}
                                />
                                <FormControlLabel
                                  control={<Switch size="small" checked={it.basico} onChange={(e) => alterarItem(i, { basico: e.target.checked })} inputProps={{ 'aria-label': `Básico: ${nome}` }} />}
                                  label={<Typography variant="caption">Básico</Typography>}
                                />
                                {it.basico && (
                                  <TextField label="Código básico" size="small" value={it.codigoBasico ?? ''}
                                    onChange={(e) => alterarItem(i, { codigoBasico: e.target.value })}
                                    error={!!erroDe(campo('codigoBasico'))}
                                    helperText={erroDe(campo('codigoBasico')) ?? 'ex.: CONTRATO_SOCIAL'} />
                                )}
                              </Stack>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <TextField label="Validade (dias)" type="number" size="small" fullWidth
                                value={it.validadeDias ?? ''}
                                onChange={(e) => alterarItem(i, { validadeDias: e.target.value === '' ? null : Number(e.target.value) })}
                                error={!!erroDe(campo('validadeDias'))} helperText={erroDe(campo('validadeDias'))} />
                            </Grid>
                            <Grid item xs={6} md={9}>
                              <TextField label="Fonte da validade" size="small" fullWidth value={it.fonteValidade ?? ''}
                                onChange={(e) => alterarItem(i, { fonteValidade: e.target.value })}
                                error={!!erroDe(campo('fonteValidade'))}
                                helperText={erroDe(campo('fonteValidade')) ?? 'Obrigatória quando há validade'} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField label="Orientação ao consultor" size="small" fullWidth multiline minRows={1}
                                value={it.orientacao ?? ''}
                                onChange={(e) => alterarItem(i, { orientacao: e.target.value })}
                                error={!!erroDe(campo('orientacao'))} helperText={erroDe(campo('orientacao'))} />
                            </Grid>
                          </Grid>
                        </Card>
                      )
                    })}
                  </Stack>
                </Box>
              </Stack>
            )
          )}
        </SecaoProduto>
      )}

      {/* Histórico */}
      <SecaoProduto
        titulo="Histórico de versões"
        descricao="Versões publicadas e propostas rejeitadas, da mais nova para a mais antiga (somente leitura)."
      >
        {historico.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma versão publicada.
          </Typography>
        ) : (
          <Table size="small" aria-label="Histórico de versões">
            <TableHead>
              <TableRow>
                <TableCell>Versão</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Vigência</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {historico.map((v) => (
                <React.Fragment key={v.id}>
                  <TableRow>
                    <TableCell>{v.versao}</TableCell>
                    <TableCell>
                      <Chip size="small" color={COR_STATUS[v.status] ?? 'default'} label={ROTULO_STATUS[v.status] ?? v.status} />
                    </TableCell>
                    <TableCell>
                      {v.vigenteDesde ? formatDate(v.vigenteDesde) : '—'}
                      {v.vigenteAte ? ` – ${formatDate(v.vigenteAte)}` : ''}
                    </TableCell>
                    <TableCell>{ROTULO_ORIGEM[v.origem] ?? v.origem}</TableCell>
                    <TableCell>
                      {v.status === 'REJEITADA' && v.motivoRejeicao ? `Rejeitada: ${v.motivoRejeicao}` : (v.motivo ?? '—')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={`${versaoAberta === v.id ? 'Ocultar' : 'Ver'} versão ${v.versao}`}
                        onClick={() => setVersaoAberta((a) => (a === v.id ? null : v.id))}
                      >
                        {versaoAberta === v.id ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: versaoAberta === v.id ? undefined : 'none' }}>
                      <Collapse in={versaoAberta === v.id} unmountOnExit>
                        <Box sx={{ py: 1.5 }}>
                          <ConteudoLeitura conteudo={v.conteudo} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </SecaoProduto>

      {/* Publicar */}
      <Dialog open={publicarAberto} onClose={() => setPublicarAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar versão {rascunho?.versao} para aprovação</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Diferenca resumo={resumo} />
            <Alert severity="info">
              A versão {rascunho?.versao} só passa a valer quando outra pessoa (gerente ou administrador) aprovar.
              {vigente
                ? ` Reservas já criadas continuam na versão ${vigente.versao}; depois da aprovação, só as novas reservas usam a versão ${rascunho?.versao}.`
                : ''}
            </Alert>
            <TextField
              label="Motivo da publicação"
              value={motivoPublicar}
              onChange={(e) => setMotivoPublicar(e.target.value)}
              multiline
              minRows={2}
              required
              inputProps={{ maxLength: 500 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublicarAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void publicar()} disabled={salvando || !motivoPublicar.trim()}>
            Enviar para aprovação
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aprovar / rejeitar a proposta (V64) */}
      <Dialog open={decisao !== null} onClose={() => setDecisao(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {decisao === 'aprovar' ? `Aprovar versão ${pendente?.versao}` : `Rejeitar versão ${pendente?.versao}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {decisao === 'aprovar' ? (
              <Alert severity="info">
                A versão {pendente?.versao} passa a valer agora para as novas reservas
                {vigente ? `; a versão ${vigente.versao} vira histórico e as reservas já criadas continuam nela` : ''}.
              </Alert>
            ) : (
              <TextField
                label="Motivo da rejeição"
                value={motivoRejeicao}
                onChange={(e) => setMotivoRejeicao(e.target.value)}
                multiline
                minRows={2}
                required
                inputProps={{ maxLength: 500 }}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisao(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color={decisao === 'aprovar' ? 'success' : 'error'}
            onClick={() => void decidir()}
            disabled={salvando || (decisao === 'rejeitar' && !motivoRejeicao.trim())}
          >
            {decisao === 'aprovar' ? 'Aprovar' : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Descartar */}
      <Dialog open={descartarAberto} onClose={() => setDescartarAberto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Descartar rascunho?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            As alterações do rascunho serão perdidas. As versões publicadas não são tocadas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDescartarAberto(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void descartar()} disabled={salvando}>
            Descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Atualizar reservas em rascunho */}
      <Dialog open={atualizarAberto} onClose={() => setAtualizarAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Atualizar reservas em rascunho</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              <AlertTitle>Ação explícita e auditada</AlertTitle>
              As reservas em RASCUNHO deste produto passam a seguir a versão vigente
              {vigente ? ` (${vigente.versao})` : ''}. Cada reserva ganha um registro no histórico.
            </Alert>
            <FormControlLabel
              control={
                <Checkbox checked={somenteSemDocumentos} onChange={(e) => setSomenteSemDocumentos(e.target.checked)} />
              }
              label="Somente reservas sem documentos enviados"
            />
            <TextField
              label="Motivo"
              value={motivoAtualizar}
              onChange={(e) => setMotivoAtualizar(e.target.value)}
              multiline
              minRows={2}
              required
              inputProps={{ maxLength: 500 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAtualizarAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void atualizarRascunhos()} disabled={salvando || !motivoAtualizar.trim()}>
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default ChecklistVersionadoEditor
